import { useEffect, useRef } from 'react';
import { useAuthStore } from '../store/auth.store';
import { isValidToken, getStoredTokens } from '../utils/utils';

/**
 * Hook que verifica periodicamente se o token está prestes a expirar
 * e o renova automaticamente antes que expire
 *
 * Com token de 1 hora (3600s), refresh a cada 50 minutos (3000s)
 * Renovação 10 minutos antes de expirar
 */
export function useTokenRefresh() {
  const { refreshAccessToken, setIsAuthenticated } = useAuthStore();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // 50 minutos em milissegundos (token 1h, renova 10min antes)
  const REFRESH_INTERVAL = 50 * 60 * 1000; // 3000000ms

  useEffect(() => {
    const checkAndRefreshToken = async () => {
      const { accessToken, refreshToken } = getStoredTokens();

      if (!accessToken || !refreshToken) {
        return;
      }

      // Verifica se o token está válido
      if (!isValidToken(accessToken)) {
        // console.log('Token expirado, renovando...');

        try {
          const newAccessToken = await refreshAccessToken();
          if (newAccessToken) {
            // console.log('Token renovado com sucesso');;
            setIsAuthenticated(true);
            // Dispara evento para notificar guards
            window.dispatchEvent(new CustomEvent('token-refreshed', { detail: { accessToken: newAccessToken } }));
          } else {
            console.error('Falha ao renovar token');
            setIsAuthenticated(false);
          }
        } catch (error) {
          console.error('Erro ao renovar token:', error);
          setIsAuthenticated(false);
        }
      }
    };

    // Verifica imediatamente
    checkAndRefreshToken();

    // Verifica a cada 50 minutos (10 minutos antes da expiração do token de 1 hora)
    intervalRef.current = setInterval(checkAndRefreshToken, REFRESH_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [refreshAccessToken, setIsAuthenticated]);
}
